<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { buildPortalArticleURL } from 'dashboard/helper/portalHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ArticleEditor from 'dashboard/components-next/HelpCenter/Pages/ArticleEditorPage/ArticleEditor.vue';

const props = defineProps({
  articleId: { type: [String, Number], required: true },
  portalSlug: { type: String, default: '' },
  accountId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const articleById = useMapGetter('articles/articleById');
const portalBySlug = useMapGetter('portals/portalBySlug');
const isFetching = useMapGetter('articles/isFetching');

const isUpdating = ref(false);
const isSaved = ref(false);

const article = computed(() => articleById.value(props.articleId));
const portal = computed(() =>
  props.portalSlug ? portalBySlug.value(props.portalSlug) : null
);

const articleLink = computed(() => {
  if (!article.value?.category || !props.portalSlug) return '';
  const { slug: categorySlug, locale: categoryLocale } = article.value.category;
  const { slug: articleSlugValue } = article.value;
  return buildPortalArticleURL(
    props.portalSlug,
    categorySlug,
    categoryLocale,
    articleSlugValue,
    portal.value?.custom_domain
  );
});

const saveArticle = async ({ ...values }, isAsync = false) => {
  const actionToDispatch = isAsync ? 'articles/updateAsync' : 'articles/update';

  if (!isAsync) isUpdating.value = true;

  try {
    await store.dispatch(actionToDispatch, {
      portalSlug: props.portalSlug,
      articleId: props.articleId,
      ...values,
    });
    if (!isAsync) isSaved.value = true;
  } catch (error) {
    if (!isAsync) isSaved.value = false;
    const errorMessage =
      error?.message || t('HELP_CENTER.EDIT_ARTICLE_PAGE.API.ERROR');
    useAlert(errorMessage);
  } finally {
    if (!isAsync) isUpdating.value = false;
  }
};

const saveArticleAsync = async values => {
  saveArticle({ ...values }, true);
};

const previewArticle = () => {
  if (articleLink.value) window.open(articleLink.value, '_blank');
};

onMounted(() => {
  if (props.portalSlug) {
    store.dispatch('articles/show', {
      id: props.articleId,
      portalSlug: props.portalSlug,
    });
  }
});
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div
      v-if="!portalSlug"
      class="flex items-center justify-center h-32 px-4 text-center text-n-slate-11 text-sm"
    >
      {{ t('COPILOT.PANEL.ARTICLE_PORTAL_REQUIRED') }}
    </div>
    <div
      v-else-if="isFetching && !article"
      class="flex items-center justify-center h-32 text-n-slate-11 text-sm"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin mr-2" />
      {{ t('COPILOT.PANEL.LOADING') }}
    </div>
    <ArticleEditor
      v-else-if="article"
      :article="article"
      :is-updating="isUpdating"
      :is-saved="isSaved"
      @save-article="saveArticle"
      @save-article-async="saveArticleAsync"
      @preview-article="previewArticle"
      @go-back="() => {}"
    />
  </div>
</template>
